// ============================================================================
// PacketReviewDrawer — Phase 1.5 (+ inline preview)
//
// Two-pane slide-over for gp_employee / intake_coordinator:
//   LEFT  = inline document preview workspace (PDF iframe / image / fallback)
//   RIGHT = document list with type dropdowns + View buttons
//
// Strict PHI rules:
//   - No original filenames are shown (server never returns them).
//   - Document preview uses a short-lived signed GET URL (Cloud Function) held
//     ONLY in component state — never persisted, never shown as a raw URL.
//   - Clicking View renders the document inline (no new browser tab).
//   - Type changes persist via a Cloud Function; per-row saving/saved/error
//     state is shown. On save failure the old value is kept (no false success).
// ============================================================================

import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { safeUserError } from '../../core/safeError.js';
import { X, FileText, Image as ImageIcon, Eye, Loader2, AlertCircle, CheckCircle2, RotateCcw, RotateCw, ScanLine, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import {
  listPacketDocuments,
  createPacketDocumentDownloadUrl,
  updatePacketDocumentType,
  requestPacketDocumentExtraction,
  resolveDocumentTypeLabel,
  getDocumentScanStatus,
  getDocumentTypeProvenance,
  PACKET_DOCUMENT_CLASSIFICATIONS,
  PACKET_DOCUMENT_CLASSIFICATION_LABELS,
  DEFAULT_PACKET_DOCUMENT_CLASSIFICATION,
  OCR_EXTRACTION_STATUS,
  OCR_IN_FLIGHT_STATUSES,
} from '../../core/store.packets.js';

// Tailwind text color per scan-status tone.
const SCAN_TONE_CLASS = {
  ok:    'text-emerald-600',
  busy:  'text-indigo-600',
  error: 'text-red-600',
  muted: 'text-slate-400',
};
import PacketPdfPreview from './PacketPdfPreview.jsx';
import { useAuth } from '../../auth/AuthContext.jsx';
import { isGpEmployee, isIntakeCoordinator } from '../../core/index.js';

const TYPE_TO_ICON = { PHOTO: ImageIcon, IMAGING: ImageIcon };

const OCR_POLL_MS = 2500;

// Human label for the OCR status line (status-only; no candidate values).
// Candidates are SUGGESTIONS reviewed/confirmed by the GP in the Gate — never
// confirmed referral truth here. Copy mirrors store.packets getDocumentScanStatus.
const ocrStatusLine = (doc) => {
  const s = doc.extractionStatus || OCR_EXTRACTION_STATUS.NOT_RUN;
  const count = doc.extractionCandidateCount;
  switch (s) {
    case OCR_EXTRACTION_STATUS.QUEUED:       return 'Extraction pending.';
    case OCR_EXTRACTION_STATUS.PROCESSING:   return 'Scanning…';
    case OCR_EXTRACTION_STATUS.COMPLETED:
      return (count && count > 0)
        ? `Scanned · ${count} suggestion${count === 1 ? '' : 's'} — review during referral creation`
        : 'No candidates found. Review the document manually.';
    case OCR_EXTRACTION_STATUS.FAILED:       return 'Extraction failed — rerun extraction or review manually.';
    case OCR_EXTRACTION_STATUS.NEEDS_REVIEW: return 'Needs review';
    default:                                 return 'Not scanned';
  }
};

const mimeKind = (mime) => {
  if (!mime) return 'File';
  if (mime === 'application/pdf') return 'PDF';
  if (mime.startsWith('image/')) return 'Image';
  return 'File';
};

const isPdfMime   = (mime) => mime === 'application/pdf';
const isImageMime = (mime) => typeof mime === 'string' && mime.startsWith('image/');

const PacketReviewDrawer = ({ packet, onClose }) => {
  const packetId = packet?.packetId;
  const { user } = useAuth();
  const role = user?.role;
  // OCR run / re-run is restricted to the roles allowed to request extraction
  // (gp_employee, intake_coordinator) — the same roles that can open this drawer.
  const canRunOcr = isGpEmployee(role) || isIntakeCoordinator(role);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [docs, setDocs]       = useState([]);
  // Per-row UI state keyed by documentId: { saveState, saveError, viewState, viewError }
  const [rowState, setRowState] = useState({});

  // ── Inline preview state (left pane). ─────────────────────────────────────
  // The signed GCS URL is fetched into a Blob and rendered via a blob: object
  // URL. This (a) satisfies the app CSP (frame-src allows blob: but NOT
  // storage.googleapis.com) and (b) keeps the signed URL out of the DOM.
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewState, setPreviewState] = useState('idle'); // 'idle'|'loading'|'ready'|'error'
  const [previewUrl, setPreviewUrl]     = useState(null);   // blob: object URL
  const [previewMime, setPreviewMime]   = useState(null);
  const [previewError, setPreviewError] = useState(null);
  // Tracks the currently-displayed blob object URL so we can revoke it on
  // replace/unmount. We always create the NEW blob URL and display it BEFORE
  // revoking the old one, so the URL bound to the live <object>/<img> is never
  // revoked while on screen (revoking a displayed blob URL is what triggers
  // Chrome's "Not allowed to load local resource: blob:" error).
  const blobUrlRef = useRef(null);
  // Most recent View request; lets the async result discard itself if the user
  // clicked another document meanwhile (no nested setState needed).
  const latestRequestRef = useRef(null);

  // ── Zoom + rotation (shared by image + PDF). zoom 1 = fit-to-pane. ────────
  const ZOOM_MIN = 0.25, ZOOM_MAX = 4, ZOOM_STEP = 0.1;
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0); // 0|90|180|270
  const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
  const zoomIn  = () => setZoom((z) => clampZoom(+(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom((z) => clampZoom(+(z - ZOOM_STEP).toFixed(2)));
  const fitView = () => { setZoom(1); setRotation(0); };
  const rotateLeft  = () => setRotation((r) => (r + 270) % 360);
  const rotateRight = () => setRotation((r) => (r + 90) % 360);
  const setZoomPercent = (pct) => {
    const n = Number(pct);
    if (Number.isFinite(n) && n > 0) setZoom(clampZoom(n / 100));
  };
  // Editable % box — local string, applied on blur / Enter.
  const [zoomInput, setZoomInput] = useState('100');
  useEffect(() => { setZoomInput(String(Math.round(zoom * 100))); }, [zoom]);

  // ── Bounded overlay: start after the left sidebar, end before the drawer ──
  // The drawer is `fixed`; we offset its left/top to the <main> content box so
  // the preview never renders under the sidebar.
  const overlayRef = useRef(null);
  const [bounds, setBounds] = useState({ left: 0, top: 0 });
  useLayoutEffect(() => {
    const el = overlayRef.current;
    if (!el) return undefined;
    const mainEl = el.closest('main') || document.querySelector('main');
    if (!mainEl) return undefined;
    const measure = () => {
      const r = mainEl.getBoundingClientRect();
      setBounds({ left: Math.max(0, Math.round(r.left)), top: Math.max(0, Math.round(r.top)) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(mainEl);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  const revokeBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  // Revoke any outstanding blob URL when the drawer unmounts.
  useEffect(() => revokeBlobUrl, [revokeBlobUrl]);

  // Reset zoom/rotation to fit whenever a different document is previewed.
  useEffect(() => { setZoom(1); setRotation(0); }, [previewDocId]);

  const loadDocs = useCallback(() => {
    if (!packetId) return;
    setLoading(true);
    setError(null);
    listPacketDocuments(packetId)
      .then((res) => setDocs(res?.documents || []))
      .catch((err) => {
        console.error('[PacketReviewDrawer] listPacketDocuments failed:', err);
        setError(safeUserError(err));
      })
      .finally(() => setLoading(false));
  }, [packetId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // Silent refresh (no list spinner) — used by OCR status polling.
  const refreshDocs = useCallback(async () => {
    if (!packetId) return;
    try {
      const res = await listPacketDocuments(packetId);
      setDocs(res?.documents || []);
    } catch (err) {
      console.error('[PacketReviewDrawer] OCR status refresh failed:', err);
    }
  }, [packetId]);

  // Phase 2A — poll while any document's OCR job is QUEUED/PROCESSING. Stops as
  // soon as no document is in flight (COMPLETED/FAILED/NEEDS_REVIEW/NOT_RUN).
  useEffect(() => {
    const anyInFlight = docs.some((d) => OCR_IN_FLIGHT_STATUSES.includes(d.extractionStatus));
    if (!anyInFlight) return undefined;
    const id = setInterval(refreshDocs, OCR_POLL_MS);
    return () => clearInterval(id);
  }, [docs, refreshDocs]);

  // Defensive: if the packet identity changes, drop any stale preview URL.
  useEffect(() => {
    latestRequestRef.current = null;
    setPreviewDocId(null);
    setPreviewState('idle');
    setPreviewUrl(null);
    setPreviewMime(null);
    setPreviewError(null);
    revokeBlobUrl();
  }, [packetId, revokeBlobUrl]);

  const patchRow = (documentId, patch) =>
    setRowState((prev) => ({ ...prev, [documentId]: { ...prev[documentId], ...patch } }));

  // Phase 2A — request an OCR extraction run. Status-only; no candidate values
  // are shown and no Patient/Gate fields are auto-filled.
  const handleRunExtraction = async (doc) => {
    patchRow(doc.documentId, { ocrState: 'requesting', ocrError: null });
    try {
      await requestPacketDocumentExtraction(packetId, doc.documentId);
      // Optimistically reflect QUEUED so the poll effect starts immediately.
      setDocs((prev) => prev.map((d) =>
        d.documentId === doc.documentId ? { ...d, extractionStatus: OCR_EXTRACTION_STATUS.QUEUED } : d));
      patchRow(doc.documentId, { ocrState: 'idle', ocrError: null });
      refreshDocs();
    } catch (err) {
      console.error('[PacketReviewDrawer] requestPacketDocumentExtraction failed:', err);
      patchRow(doc.documentId, { ocrState: 'error', ocrError: safeUserError(err) });
    }
  };

  const handleTypeChange = async (doc, nextType) => {
    const prevType = doc.documentType || DEFAULT_PACKET_DOCUMENT_CLASSIFICATION;
    if (nextType === prevType) return;
    // Optimistic display, but mark as "saving" — we revert on failure.
    patchRow(doc.documentId, { saveState: 'saving', saveError: null });
    setDocs((prev) => prev.map((d) => d.documentId === doc.documentId ? { ...d, documentType: nextType } : d));
    try {
      await updatePacketDocumentType(packetId, doc.documentId, nextType);
      patchRow(doc.documentId, { saveState: 'saved', saveError: null });
    } catch (err) {
      console.error('[PacketReviewDrawer] updatePacketDocumentType failed:', err);
      // Revert to previous value — do NOT show as saved.
      setDocs((prev) => prev.map((d) => d.documentId === doc.documentId ? { ...d, documentType: prevType } : d));
      patchRow(doc.documentId, { saveState: 'error', saveError: safeUserError(err) });
    }
  };

  // View renders the document INLINE in the left pane (no new tab).
  // The signed URL is fetched into a Blob and shown via a blob: object URL so
  // the raw storage.googleapis.com URL never enters the DOM (and the app CSP,
  // which allows blob: but not storage.googleapis.com in frame-src, is honored).
  const handleView = async (doc) => {
    const requestId = doc.documentId;
    latestRequestRef.current = requestId;
    // Switch the pane to loading WITHOUT touching the old blob yet — the old
    // <object> unmounts as state flips, and we only revoke once the new blob is
    // live (never revoke a URL that is still bound to a rendered element).
    setPreviewDocId(requestId);
    setPreviewState('loading');
    setPreviewUrl(null);
    setPreviewMime(null);
    setPreviewError(null);
    patchRow(requestId, { viewState: 'loading', viewError: null });
    try {
      const { downloadUrl, mimeType } = await createPacketDocumentDownloadUrl(packetId, requestId);
      // Fetch bytes (the signed storage.googleapis.com URL is allowed by the CSP
      // connect-src allowlist) and wrap as a blob: URL. NOTE: connect-src must
      // also allow blob: so pdf.js can fetch this object URL to render PDFs.
      const res = await fetch(downloadUrl);
      if (!res.ok) throw new Error('Preview fetch failed (' + res.status + ')');
      const blob = await res.blob();

      // Race guard: a newer View click supersedes this result — discard it.
      if (latestRequestRef.current !== requestId) return;

      const objectUrl = URL.createObjectURL(blob);
      const previous = blobUrlRef.current;   // currently-displayed url (if any)
      blobUrlRef.current = objectUrl;
      setPreviewMime(mimeType || blob.type || doc.mimeType || null);
      setPreviewUrl(objectUrl);              // bind the NEW url first…
      setPreviewState('ready');
      if (previous) URL.revokeObjectURL(previous); // …then revoke the OLD one
      patchRow(requestId, { viewState: 'idle', viewError: null });
    } catch (err) {
      console.error('[PacketReviewDrawer] packet document preview failed:', err);
      if (latestRequestRef.current !== requestId) return;
      // Do not mark as viewed; surface error in both the row and the preview pane.
      setPreviewState('error');
      setPreviewError(safeUserError(err));
      patchRow(requestId, { viewState: 'error', viewError: safeUserError(err) });
    }
  };

  const previewIndex = previewDocId != null ? docs.findIndex((d) => d.documentId === previewDocId) : -1;
  const previewDoc   = previewIndex >= 0 ? docs[previewIndex] : null;

  const clearPreview = () => {
    latestRequestRef.current = null;
    setPreviewDocId(null); setPreviewUrl(null); setPreviewState('idle'); setPreviewError(null);
    revokeBlobUrl();
  };
  const isZoomable = previewState === 'ready' && previewUrl && (isImageMime(previewMime) || isPdfMime(previewMime));

  return (
    // Bounded to the <main> content box (left = after sidebar, top = below any
    // header). right/bottom anchored to the viewport edge. Never under the sidebar.
    <div ref={overlayRef} className="fixed right-0 bottom-0 z-50 flex" style={{ left: bounds.left, top: bounds.top }}>
      {/* LEFT — dedicated bounded preview pane */}
      <div className="flex-1 min-w-0 bg-slate-100 flex flex-col border-r border-slate-300">
        {previewDoc ? (
          <>
            {/* Top bar: document label + zoom/rotate controls + Clear preview */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-white text-sm text-slate-700 flex-wrap">
              <FileText className="w-4 h-4 text-slate-400 shrink-0" />
              <span className="font-medium text-slate-800">Document {previewIndex + 1}</span>
              <span className="text-slate-400">· {mimeKind(previewMime || previewDoc.mimeType)}</span>
              <span className="text-slate-400">· {resolveDocumentTypeLabel(previewDoc)}</span>

              {isZoomable && (
                <div className="flex items-center gap-1 ml-2">
                  <button type="button" onClick={zoomOut} disabled={zoom <= ZOOM_MIN}
                    className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" aria-label="Zoom out" title="Zoom out">
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <div className="flex items-center">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={zoomInput}
                      onChange={(e) => setZoomInput(e.target.value.replace(/[^\d]/g, ''))}
                      onBlur={() => setZoomPercent(zoomInput)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setZoomPercent(zoomInput); e.currentTarget.blur(); } }}
                      className="w-12 text-center tabular-nums border border-slate-300 rounded px-1 py-0.5 text-xs"
                      aria-label="Zoom percentage"
                    />
                    <span className="text-xs text-slate-500 ml-0.5">%</span>
                  </div>
                  <button type="button" onClick={zoomIn} disabled={zoom >= ZOOM_MAX}
                    className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" aria-label="Zoom in" title="Zoom in">
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={fitView}
                    className="p-1 rounded hover:bg-slate-100" aria-label="Fit to window" title="Fit to window">
                    <Maximize2 className="w-4 h-4" />
                  </button>
                  <span className="w-px h-4 bg-slate-200 mx-1" />
                  <button type="button" onClick={rotateLeft}
                    className="p-1 rounded hover:bg-slate-100" aria-label="Rotate left" title="Rotate left">
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={rotateRight}
                    className="p-1 rounded hover:bg-slate-100" aria-label="Rotate right" title="Rotate right">
                    <RotateCw className="w-4 h-4" />
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={clearPreview}
                className="ml-auto text-xs text-slate-500 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-100"
              >
                Clear preview
              </button>
            </div>

            {/* PDF (ready) renders its own fit-to-pane surface + page nav.
                Loading / error / image / unsupported render in a bounded area. */}
            {previewState === 'ready' && previewUrl && isPdfMime(previewMime) ? (
              <PacketPdfPreview
                key={previewUrl}
                url={previewUrl}
                zoom={zoom}
                rotation={rotation}
                onRetry={() => handleView(previewDoc)}
              />
            ) : (
              <div className="flex-1 min-h-0 overflow-auto bg-slate-100 flex items-center justify-center p-4">
                {previewState === 'loading' && (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading preview…
                  </div>
                )}

                {previewState === 'error' && (
                  <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700 max-w-sm">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium">Preview failed</div>
                        <div>{previewError}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleView(previewDoc)}
                      className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-red-300 rounded-md text-red-700 hover:bg-red-100"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Retry
                    </button>
                  </div>
                )}

                {previewState === 'ready' && previewUrl && isImageMime(previewMime) && (
                  <img
                    src={previewUrl}
                    alt="Document preview"
                    className="max-w-full max-h-full object-contain select-none"
                    style={{ transform: `rotate(${rotation}deg) scale(${zoom})`, transformOrigin: 'center', transition: 'transform 120ms ease-out' }}
                    draggable={false}
                  />
                )}

                {previewState === 'ready' && previewUrl && !isPdfMime(previewMime) && !isImageMime(previewMime) && (
                  <div className="text-center text-sm text-slate-500 max-w-sm">
                    <div className="mb-2">Preview is not available for this file type.</div>
                    <div className="text-xs text-slate-400">{previewMime || 'unknown type'}</div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
            Select a document to preview.
          </div>
        )}
      </div>

      {/* RIGHT — document list / review drawer */}
      <div className="relative w-full max-w-lg h-full bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Review Documents</h2>
            <p className="text-xs text-slate-500">Packet · {packetId?.slice(-8)}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100" aria-label="Close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading documents…
            </div>
          )}

          {error && !loading && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium">Could not load documents</div>
                  <div>{error}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={loadDocs}
                className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-red-300 rounded-md text-red-700 hover:bg-red-100"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Retry
              </button>
            </div>
          )}

          {!loading && !error && docs.length === 0 && (
            <div className="text-sm text-slate-500">No documents in this packet yet.</div>
          )}

          {!loading && !error && docs.length > 0 && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
              {isGpEmployee(role)
                ? 'OCR suggestions are extracted from these documents. Start a referral from this packet to review and confirm them in the Gate — they are suggestions, not confirmed referral information.'
                : 'OCR suggestions are extracted from these documents. They are reviewed and confirmed by the referring provider in the Gate — not confirmed referral information here.'}
            </div>
          )}

          {!loading && !error && docs.map((d, idx) => {
            const Icon = TYPE_TO_ICON[d.type] || FileText;
            const rs = rowState[d.documentId] || {};
            const isPromoted = d.status === 'PROMOTED';
            const isReady = d.status === 'READY' || d.status === 'PROMOTED' || d.status === 'PROMOTING';
            const isPreviewing = previewDocId === d.documentId;
            return (
              <div
                key={d.documentId}
                className={'rounded-lg border p-3 space-y-2 ' + (isPreviewing ? 'border-indigo-400 ring-1 ring-indigo-200' : 'border-slate-200')}
              >
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <Icon className="w-4 h-4 text-slate-500" />
                  <span className="font-medium text-slate-800">Document {idx + 1}</span>
                  <span className="text-slate-500">· {mimeKind(d.mimeType)}</span>
                  {d.sizeBytes != null && (
                    <span className="text-xs text-slate-400">· {Math.round(d.sizeBytes / 1024)} KB</span>
                  )}
                  {/* READY · Scanned · N suggestions (status & counts only — no PHI). */}
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px]">
                    <span className="uppercase tracking-wide text-slate-400">{d.status}</span>
                    {isReady && (() => {
                      const scan = getDocumentScanStatus(d);
                      return <span className={'normal-case ' + (SCAN_TONE_CLASS[scan.tone] || 'text-slate-400')}>· {scan.label}</span>;
                    })()}
                  </span>
                </div>

                {/* Type provenance — Auto-detected / Confirmed / Review type (no PHI). */}
                {(() => {
                  const prov = getDocumentTypeProvenance(d);
                  return (
                    <div className="text-[11px] text-slate-500">
                      Type: <span className="text-slate-700">{prov.typeLabel}</span>
                      <span className="text-slate-400"> · {prov.source}</span>
                    </div>
                  );
                })()}

                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-500 shrink-0">Type</label>
                  <select
                    value={d.documentType || DEFAULT_PACKET_DOCUMENT_CLASSIFICATION}
                    disabled={isPromoted || rs.saveState === 'saving'}
                    onChange={(e) => handleTypeChange(d, e.target.value)}
                    className="flex-1 text-sm border border-slate-300 rounded-md px-2 py-1 bg-white disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    {PACKET_DOCUMENT_CLASSIFICATIONS.map((c) => (
                      <option key={c} value={c}>{PACKET_DOCUMENT_CLASSIFICATION_LABELS[c]}</option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => handleView(d)}
                    disabled={!isReady || rs.viewState === 'loading'}
                    className={'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border rounded-md disabled:opacity-40 ' +
                      (isPreviewing ? 'border-indigo-400 text-indigo-700 bg-indigo-50' : 'border-slate-300 text-slate-700 hover:bg-slate-50')}
                    title={isReady ? 'Preview document in the left panel' : 'Document is not ready to view'}
                  >
                    {rs.viewState === 'loading'
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Eye className="w-3.5 h-3.5" />}
                    {isPreviewing ? 'Previewing' : 'View'}
                  </button>
                </div>

                <div className="flex items-center gap-3 min-h-[16px]">
                  {rs.saveState === 'saving' && (
                    <span className="text-xs text-indigo-600 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                    </span>
                  )}
                  {rs.saveState === 'saved' && (
                    <span className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Saved
                    </span>
                  )}
                  {rs.saveState === 'error' && (
                    <span className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {rs.saveError || 'Save failed'}
                    </span>
                  )}
                  {isPromoted && (
                    <span className="text-xs text-slate-400">Type locked — document already assigned to a referral.</span>
                  )}
                  {rs.viewError && (
                    <span className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {rs.viewError}
                    </span>
                  )}
                </div>

                {/* Phase 2A — OCR status row (status-only; no candidate values) */}
                {(() => {
                  const ocrStatus = d.extractionStatus || OCR_EXTRACTION_STATUS.NOT_RUN;
                  const inFlight = OCR_IN_FLIGHT_STATUSES.includes(ocrStatus);
                  const requesting = rs.ocrState === 'requesting';
                  const canRun = isReady && !inFlight && !requesting;
                  const runLabel =
                    ocrStatus === OCR_EXTRACTION_STATUS.NOT_RUN ? 'Run extraction' :
                    ocrStatus === OCR_EXTRACTION_STATUS.FAILED  ? 'Retry' :
                    'Re-run extraction';
                  const statusColor =
                    ocrStatus === OCR_EXTRACTION_STATUS.COMPLETED ? 'text-emerald-600' :
                    ocrStatus === OCR_EXTRACTION_STATUS.FAILED    ? 'text-red-600' :
                    inFlight                                      ? 'text-indigo-600' :
                    'text-slate-500';
                  return (
                    <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                      <ScanLine className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="text-xs text-slate-500 shrink-0">OCR:</span>
                      <span className={'text-xs flex items-center gap-1 ' + statusColor}>
                        {inFlight && <Loader2 className="w-3 h-3 animate-spin" />}
                        {ocrStatusLine(d)}
                      </span>
                      {!inFlight && canRunOcr && (
                        <button
                          type="button"
                          onClick={() => handleRunExtraction(d)}
                          disabled={!canRun}
                          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                          title={isReady ? 'Run the OCR extraction pipeline for this document' : 'Document must be READY to run extraction'}
                        >
                          {requesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
                          {runLabel}
                        </button>
                      )}
                    </div>
                  );
                })()}

                {rs.ocrError && (
                  <div className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {rs.ocrError}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="p-4 border-t border-slate-200 text-xs text-slate-500">
          Typing documents is recommended but not required to start a referral.
        </div>
      </div>
    </div>
  );
};

export default PacketReviewDrawer;
